import React from 'react';
import './App.css';
import { useSelector } from 'react-redux';
import {Navigate, Outlet, RouterProvider} from 'react-router';
import Footer from './components/Footer/Footer';
import { createBrowserRouter } from 'react-router-dom';
import LoadingSpinner from './components/widgets/LoadingSpinner';
import Error500 from './containers/Errors/Error500';
import Error404 from './containers/Errors/Error404';
import Dashboard from './containers/Dashboard/Dashboard';
import { ProtectedRoutes } from './routes/ProtectedRoutes';
import { OnboardingRoute } from './routes/OnboardingRoute';
import { OauthCallback } from './components/auth/OauthCallback';
import Logout from './containers/auth/Logout';
import Home from './containers/Home/Home';
import AccountCompletion from './containers/auth/AccountCompletion';
import './i18n';
import Navbar from './components/navigation/Navbar';
import Countries from './containers/Countries/Countries';
import CodeList from './containers/CodeList/CodeList';
import Users from './containers/Users/Users';
import EditUser from './containers/Users/EditUser';
import SystemSettings from './containers/SystemSettings/SystemSettings';
import { useTranslation } from 'react-i18next';
import ActivityLog from './containers/ActivityLog/ActivityLog';
import IdleTimerCheck from './routes/IdleTimerCheck';
import APIKeys from './containers/apikeys/APIKeys';
import JsonApiLog from './containers/JsonApiLog/JsonApiLog';
import IDPSettings from "./containers/IDPSettings/IDPSettings";
import {OpenIDCallback} from "./components/auth/OpenIDCallback";
import PermissionRoute from './routes/PermissionRoute';

function App() {

  const { i18n } = useTranslation();
  document.body.dir = i18n.dir();

  const authState = useSelector((state) => state.auth);

  const router = createBrowserRouter([
    {
      path: "/",
      errorElement: <Error500 />,
      element: (
        <IdleTimerCheck>
          <main className="main" id="top">
            <div className="container-fluid">
              <div className="row">
                <div className="col-md-12">
                  <Navbar />
                </div>
              </div>
              {authState.loading
                ? <LoadingSpinner />
                : <Outlet />
              }
            </div>
            <Footer />
          </main>
        </IdleTimerCheck>)
      ,
      id: "root",
      children: [
        {
          index: true,
          element: <Navigate to="/login" replace />
        },
        {
          path: 'login',
          children: [
            {
              index: true,
              element: <Home isLoggedIn={authState.isLoggedIn} /> // Sign in page
            },
            {
              path: ':countryCode',
              element: <Home isLoggedIn={authState.isLoggedIn} />
            }
          ]
        },
        {
          path: 'logout',
          element: <Logout /> // Logout component to allow logout by URL
        },
        {
          path: "auth/OauthCallback",
          element: <OauthCallback />, // Receive Oauth code (This is the OAuth client redirect_uri)
        },
        {
          path: "auth/openid/callback",
          element: <OpenIDCallback />, // Receive Oauth code (This is the OAuth client redirect_uri)
        },
        {
          path: "account/setup",
          element: <OnboardingRoute />,       // check only for authentication
          children: [
            {
              index: true,
              element: <AccountCompletion />, // request user to complete account
            }
          ]
        },
        {
          path: "app",
          element: <ProtectedRoutes />,   // check for authentication & user account 
          children: [
            {
              path: "activity",
              element: <ActivityLog />
            },
            {
              path: "dashboard",
              element: <Dashboard />
            },
            {
              path: "countries",
              element: <PermissionRoute permission="canManageSettings" />,
              children: [
                {
                  index: true,
                  element: <Countries />
                }
              ]
            },
            {
              path: "codelists",
              element: <PermissionRoute permission="canManageSettings" />,
              children: [
                {
                  index: true,
                  element: <CodeList />
                }
              ]
            },
            {
              path: "settings",
              element: <PermissionRoute permission="canManageSysOptionsAndUsers" />,
              children: [
                {
                  index: true,
                  element: <SystemSettings />
                },
                {
                  path: ":countryCode",
                  element: <SystemSettings />
                }
              ]
            },
            {
              path: "apikeys",
              element: <PermissionRoute permission="canManageSettings" />,
              children: [
                {
                  index: true,
                  element: <APIKeys />
                }
              ]
            },
            {
              path: "apilog",
              element: <JsonApiLog />
            },
            {
              path: "users",
              element: <PermissionRoute permission="canManageSysOptionsAndUsers" />,
              children: [
                {
                  index: true,
                  element: <Users />
                },
                {
                  path: 'edit/:id',
                  element: <EditUser />
                }
              ]
            },
            {
              path: "idpSettings",
              element: <PermissionRoute permission="canManageSettings" />,
              children: [
                {
                  index: true,
                  element: <IDPSettings />
                },
                {
                  path: ":countryCode",
                  element: <IDPSettings />
                }
              ]
            },
          ]
        },
        {
          path: "*",
          element: <Error404 />
        },
        {
          path: "error",
          element: <Error500 />
        },
      ],
    },
  ]);

  return <RouterProvider router={router} />;
}

export default App;
